"use client";

import { useCurrentUser } from "@/hooks/use-current-user";
import { usePendingInquiry } from "@/hooks/use-pending-inquiry";
import { api } from "@/lib/api-client";
import type { DetailedListing } from "@/lib/types";
import { buildChatUrl } from "@/lib/url";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import { toast } from "sonner";

export type ListingPendingInquiryHandlerProps = {
  listing: DetailedListing;
};

export function ListingPendingInquiryHandler({
  listing,
}: ListingPendingInquiryHandlerProps) {
  const { user, loaded } = useCurrentUser();
  const { consume } = usePendingInquiry();
  const router = useRouter();
  const hasRun = useRef(false);

  const apiUtils = api.useUtils();
  const sendMessageMutation = api.listings.sendMessage.useMutation();

  useEffect(() => {
    if (!loaded || !user || hasRun.current) return;
    hasRun.current = true;

    const pending = consume();
    if (!pending || pending.type !== "listing" || pending.listingId !== listing.id) {
      return;
    }

    if (user.id === listing.ownerId || user.id === listing.sellerAgentId) {
      return;
    }

    (async () => {
      const existing =
        await apiUtils.listings.getListingInquiryConversation.fetch({
          id: listing.id,
        });

      if (existing.conversation) {
        const chatUrl = buildChatUrl(existing.conversation);
        toast.success("You've already sent an inquiry for this listing.", {
          action: {
            label: "Open conversation",
            onClick: () => router.push(chatUrl),
          },
        });
        return;
      }

      const gate = await apiUtils.listings.canInquire.fetch({
        listingId: listing.id,
      });
      if (!gate.allowed && gate.reason === "BROKER_BLOCK") {
        toast.error(
          "This broker is not accepting inquiries from you.",
        );
        return;
      }

      try {
        const { conversation } = await sendMessageMutation.mutateAsync({
          id: listing.id,
          message: pending.message,
        });

        toast.success("Your inquiry has been sent!", {
          action: {
            label: "Open conversation",
            onClick: () => router.push(buildChatUrl(conversation)),
          },
        });
      } catch (err) {
        console.error("Failed to auto-submit pending inquiry:", err);
        toast.error("We couldn't send your inquiry. Please try again.");
      }
    })();
  }, [loaded, user]);

  return null;
}
